import React, { useEffect, useState } from 'react';

export default function SimulationNameEditor({ simulation, onSave }) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setIsEditing(false);
    setDraft('');
    setError('');
  }, [simulation?.id]);

  const startEditing = () => {
    if (!simulation?.id) return;
    setDraft(simulation.name || '');
    setError('');
    setIsEditing(true);
  };

  const cancelEditing = () => {
    if (isSaving) return;
    setIsEditing(false);
    setDraft('');
    setError('');
  };

  const save = async () => {
    const nextName = draft.trim();

    if (!nextName) {
      setError('Simulation name cannot be empty.');
      return;
    }

    if (nextName === (simulation?.name || '').trim()) {
      cancelEditing();
      return;
    }

    setIsSaving(true);
    setError('');

    try {
      await onSave(simulation.id, nextName);
      setIsEditing(false);
      setDraft('');
    } catch (saveError) {
      console.error('Error updating simulation name:', saveError);
      setError('Could not save the simulation name. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isEditing) {
    return (
      <button
        type="button"
        onClick={startEditing}
        disabled={!simulation?.id}
        aria-label="Edit selected simulation name"
        title="Edit selected simulation name"
        style={{
          padding: '5px 9px',
          borderRadius: '4px',
          border: '1px solid #555',
          backgroundColor: '#3A3A3A',
          color: '#FFF',
          cursor: simulation?.id ? 'pointer' : 'default'
        }}
      >
        ✎ Rename
      </button>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
      <input
        type="text"
        value={draft}
        onChange={(event) => {
          setDraft(event.target.value);
          setError('');
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') save();
          if (event.key === 'Escape') cancelEditing();
        }}
        aria-label="Simulation name"
        autoFocus
        disabled={isSaving}
        style={{
          minWidth: '220px',
          padding: '5px 8px',
          borderRadius: '4px',
          border: `1px solid ${error ? '#ff6b6b' : '#666'}`,
          backgroundColor: '#262626',
          color: '#FFF'
        }}
      />
      <button
        type="button"
        onClick={save}
        disabled={isSaving || !draft.trim()}
        style={{ padding: '5px 9px', cursor: 'pointer' }}
      >
        {isSaving ? 'Saving…' : 'Save'}
      </button>
      <button
        type="button"
        onClick={cancelEditing}
        disabled={isSaving}
        style={{ padding: '5px 9px', cursor: 'pointer' }}
      >
        Cancel
      </button>
      {error && (
        <span role="alert" style={{ color: '#ff8a8a', fontSize: '12px' }}>
          {error}
        </span>
      )}
    </div>
  );
}
